// Hook: Trading Mind Firestore — CRUD for tradingMind collections
import { useState, useEffect, useCallback } from 'react';
import { doc, onSnapshot, setDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { DEFAULT_MIND_SETTINGS } from '../config/mindConstants';
import { getTodayStr } from '../utils/mindScoring';

// Root settings doc: tradingMind/{uid}
export function useMindSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }

    const ref = doc(db, 'tradingMind', user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setSettings({ ...DEFAULT_MIND_SETTINGS, ...snap.data() });
      } else {
        setDoc(ref, DEFAULT_MIND_SETTINGS);
        setSettings({ ...DEFAULT_MIND_SETTINGS });
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const updateSettings = useCallback(async (data) => {
    const user = auth.currentUser;
    if (!user) return;
    const ref = doc(db, 'tradingMind', user.uid);
    await setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true });
  }, []);

  return { settings, loading, updateSettings };
}

// Daily state: tradingMind/{uid}/dailyState/{YYYY-MM-DD}
export function useDailyState() {
  const [dailyState, setDailyState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }

    const today = getTodayStr();
    const ref = doc(db, 'tradingMind', user.uid, 'dailyState', today);
    const unsub = onSnapshot(ref, (snap) => {
      setDailyState(snap.exists() ? snap.data() : null);
      setLoading(false);
    });
    return unsub;
  }, []);

  const saveDailyState = useCallback(async (data) => {
    const user = auth.currentUser;
    if (!user) return;
    const today = getTodayStr();
    const ref = doc(db, 'tradingMind', user.uid, 'dailyState', today);
    await setDoc(ref, { ...data, date: today, updatedAt: serverTimestamp() }, { merge: true });
  }, []);

  return { dailyState, loading, saveDailyState };
}

// Performance: tradingMind/{uid}/performance/{YYYY-MM-DD}
export function usePerformanceState() {
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) { setLoading(false); return; }

    const today = getTodayStr();
    const ref = doc(db, 'tradingMind', user.uid, 'performance', today);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setPerformance(snap.data());
      } else {
        const defaults = {
          consecutiveLosses: 0,
          tradesToday: 0,
          dailyPnL: 0,
          lastTradeTime: null,
          lastTradeSymbol: null,
          lastTradePnL: null,
          cooldownUntil: null,
          recentTrades: [],
        };
        setPerformance(defaults);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const updatePerformance = useCallback(async (data) => {
    const user = auth.currentUser;
    if (!user) return;
    const today = getTodayStr();
    const ref = doc(db, 'tradingMind', user.uid, 'performance', today);
    await setDoc(ref, { ...data, date: today, updatedAt: serverTimestamp() }, { merge: true });
  }, []);

  return { performance, loading, updatePerformance };
}

// Behavior logs: tradingMind/{uid}/behaviorLogs/{auto-id}
export function useBehaviorLog() {
  const logBehavior = useCallback(async (entry) => {
    const user = auth.currentUser;
    if (!user) return;
    const ref = collection(db, 'tradingMind', user.uid, 'behaviorLogs');
    await addDoc(ref, {
      ...entry,
      timestamp: Date.now(),
      date: getTodayStr(),
      createdAt: serverTimestamp(),
    });
  }, []);

  return { logBehavior };
}
